import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatestWith, filter, map, Observable, Subject, switchMap, take, takeUntil, tap } from 'rxjs';

import { UserDetailsDto } from '../../../../../common/dtos/user-details.dto';
import { ACCOUNT_ID_PARAM } from '../../../../constants/application-routes';
import { AdobeLaunchService } from '../../../../services/adobe-launch/adobe-launch.service';
import { updateRemoveUserStepperSelectedAccounts, updateRemoveUserStepperSelectedUser } from '../../../../store/actions/remove-user-stepper-flow.action';
import * as accountPermissionsSelector from '../../../../store/selectors/account-permissions.selector';
import { selectAccountUsers } from '../../../../store/selectors/account-permissions.selector';
import * as grantRevokePermissionsSelector from '../../../../store/selectors/grant-revoke-permissions.selector';
import accountPermissionUtils from '../../../../utils/account-permission/account-permission.util';
import { AdobeTagging } from '../../../../utils/decorators/dev/adobe-launch';
import { adobeConfigData } from './raf-select-user-step-adobe';
import { AdobeAnalyticsProcessStep } from '../../../../services/adobe-launch/adobe-launch.process';
import { ContentUtil } from '../../../../utils/content/content.util';
import { ClientSegmentFacadeService } from '../../../../services/client-segment-observer/client-segment.service';
import { ClientSegmentContentDTO } from 'src/common/dtos/client-segment.dto';
import { LoggerService } from '../../../../services/logger/logger.service';
import { environment } from '../../../../../environments/environment';
import { Content } from 'src/common/interfaces/content.interface';
import { Unsubscribe } from '../../../../utils/decorators/dev';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { LinkModule } from '@vg-constellation/angular-20/link';
import { BannerModule } from '@vg-constellation/angular-20/banner';
import { InternalCalloutModule } from '@vg-constellation/angular-20/internal-callout';
import { RadioModule } from '@vg-constellation/angular-20/radio';
import { IconComponent } from '@vg-constellation/angular-20/icon';

@AdobeTagging(adobeConfigData)
@Unsubscribe
@Component({
    selector: 'zci-raf-select-user-step',
    templateUrl: './raf-select-user-step.component.html',
    styleUrls: ['./raf-select-user-step.component.scss'],
    imports: [
        AsyncPipe,
        NgFor,
        ReactiveFormsModule,
        RadioModule,
        BannerModule,
        InternalCalloutModule,
        LinkModule,
        IconComponent
    ],
})
export class RafSelectUserStepComponent
    implements OnInit, OnDestroy {
    protected readonly adobeLaunchService = inject(AdobeLaunchService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly store = inject(Store);
    private readonly logger = inject(LoggerService);
    private readonly clientSegmentFacadeService = inject(ClientSegmentFacadeService);

    @Input() nextStepClicked: EventEmitter<any> = new EventEmitter<any>();
    @Input() nextStepReady: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() locationChanged: EventEmitter<any>;

    content: Content = ContentUtil.content;
    grantorContent = ContentUtil.grantorFragmentContent;
    frmcRemoveAccessSelectUser = this.grantorContent.remove_user.frmcRemoveAccessSelectUser;

    @ViewChild('selectionError') selectionErrorElement?: ElementRef<HTMLDivElement>;

    readonly infoWaveUrl: string = environment.INFO_WAVE_URL;
    readonly isInternal: boolean = environment.isInternal;

    users$: Observable<UserDetailsDto[]>;

    radioGroupId: string = 'user-selection-group';
    showSelectionError = false;

    selectUserFormControlName: string = 'selectedUser';
    selectUserFormGroup: FormGroup = new FormGroup({
        [this.selectUserFormControlName]: new FormControl(null, [Validators.required]),
    });

    constructUserName: (user: UserDetailsDto) => string = accountPermissionUtils.constructUserName;
    errDescription: string;
    protected readonly AdobeAnalyticsProcessStep = AdobeAnalyticsProcessStep;
    private destroy$ = new Subject<void>();
    private scrollTimeoutId: ReturnType<typeof setTimeout> | null = null;

    get selectUserFormControl(): AbstractControl {
        return this.selectUserFormGroup.get(this.selectUserFormControlName);
    }

    get infowaveBoxContents(): string[] {
        const infoWaveBox = this.content?.removeAccessFlow?.selectUser?.infoWaveBox;
        return typeof infoWaveBox === 'string'
            ? infoWaveBox.split(`{{infoWaveLink}}`).map((part) => part.trim())
            : [];
    }

    /**
     * Component lifecycle
     */
    ngOnInit(): void {
        const accountId = this.activatedRoute.snapshot.params[ACCOUNT_ID_PARAM];
        const selectedUser$: Observable<UserDetailsDto> = this.store.select(
            grantRevokePermissionsSelector.selectRemoveUserStepperSelectedUser,
        );

        this.clientSegmentFacadeService.clientSegmentContent$
            .pipe(takeUntil(this.destroy$))
            .subscribe((content: ClientSegmentContentDTO) => {
                this.errDescription =
                    this.grantorContent.edit_remove_user_access.removeThisPersonContactInfo.text1.replace(
                        '${phoneNumber}',
                        content?.phoneNumber,
                    );
            });

        this.users$ = this.store.select(selectAccountUsers).pipe(
            filter((accountUsers) => accountUsers.hasLoaded),
            take(1),
            switchMap(() =>
                this.store.select(
                    accountPermissionsSelector.selectGrantedUsersOnAccount(
                        accountId,
                        this.logger.warn,
                    ),
                ).pipe(
                    combineLatestWith(selectedUser$),
                    tap(this.patchFormValueWithSelectedUser.bind(this)),
                    map(([existingUsers]) =>
                        [...existingUsers].sort((u1, u2) =>
                            /* istanbul ignore next */
                            this.constructUserName(u1) > this.constructUserName(u2) ? 1 : -1,
                        ),
                    ),
                    take(1),
                ),
            ),
        );

        this.nextStepClicked.subscribe(this.onNextStepLoad.bind(this));
    }

    onUserSelect(user: UserDetailsDto): void {
        this.showSelectionError = false;
    }

    /**
     * Moves the ui flow to the next step.
     * If the selected user has Full Authority access, the modal is shown and the stepper
     * is notified not to advance (emit false resets the stepper's waiting/loading state).
     * For all other access levels the flow advances normally.
     */
    onNextStepLoad(): void {
        this.selectUserFormControl.markAsDirty();
        if (!this.selectUserFormControl.valid) {
            this.showSelectionError = true;
            this.scrollTimeoutId = setTimeout(() => {
                this.selectionErrorElement?.nativeElement?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }, 0);
            this.nextStepReady.emit(false);
            return;
        }

        this.showSelectionError = false;
        const selectedUser: UserDetailsDto = this.selectUserFormControl.value;
        this.store.dispatch(updateRemoveUserStepperSelectedAccounts({ selectedAccounts: [] }));
        this.store.dispatch(updateRemoveUserStepperSelectedUser({ selectedUser }));
        this.nextStepReady.emit(true);
    }

    ngOnDestroy(): void {
        if (this.scrollTimeoutId) {
            clearTimeout(this.scrollTimeoutId);
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    private patchFormValueWithSelectedUser([existingUsers, selectedUser]: [
        UserDetailsDto[],
        UserDetailsDto,
    ]): void {
        if (selectedUser) {
            const selectedUserToBePatched = existingUsers.find(
                (user) => user.clientPoid === selectedUser.clientPoid,
            );
            if (selectedUserToBePatched) {
                this.selectUserFormControl.patchValue(selectedUserToBePatched);
                return;
            }
        }
    }
}
